/**
 * Where a notification leads, and whether you are already looking at it.
 *
 * A route travels through the desktop shell as an opaque string (the toast
 * hands it back on click), so it is encoded as JSON and checked again on the
 * way back: an unknown or malformed route only focuses the window.
 */
import type { Snowflake } from '@neosocial/protocol';
import { readLayout, openMain, usePanes } from '../shell/panes';
import { goHub, goSettings, useUi, type HubView } from '../store/ui';

export type NotifyRoute =
  /** A server text channel (or a voice channel's chat), at a message when given. */
  | { to: 'channel'; channelId: Snowflake; messageId?: Snowflake }
  /** A DM or group DM, at a message when given. */
  | { to: 'dm'; channelId: Snowflake; messageId?: Snowflake }
  /** A voice room. */
  | { to: 'voice'; channelId: Snowflake }
  /** Friends > Requests. */
  | { to: 'requests' };

const SNOWFLAKE = /^\d{1,20}$/;
const id = (v: unknown): v is Snowflake => typeof v === 'string' && SNOWFLAKE.test(v);

export function encodeRoute(r: NotifyRoute): string {
  return JSON.stringify(r);
}

/** The route a string carries, or null for anything not made by `encodeRoute`. */
export function decodeRoute(raw: string | null | undefined): NotifyRoute | null {
  if (!raw) return null;
  let v: unknown;
  try {
    v = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!v || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  const msg = o.messageId === undefined || id(o.messageId);
  switch (o.to) {
    case 'channel':
    case 'dm':
      if (!id(o.channelId) || !msg) return null;
      return o.messageId
        ? { to: o.to, channelId: o.channelId, messageId: o.messageId as Snowflake }
        : { to: o.to, channelId: o.channelId };
    case 'voice':
      return id(o.channelId) ? { to: 'voice', channelId: o.channelId } : null;
    case 'requests':
      return { to: 'requests' };
    default:
      return null;
  }
}

/** One conversation, one key: what the toasts are grouped by (and what "in view" compares). */
export function routeKey(r: NotifyRoute): string {
  switch (r.to) {
    case 'channel':
    case 'dm':
      return 'c:' + r.channelId;
    case 'voice':
      return 'v:' + r.channelId;
    case 'requests':
      return 'requests';
  }
}

/** The key of what the main pane shows, or null (Friends, nothing). */
export function viewKey(v: HubView | null): string | null {
  if (!v) return null;
  switch (v.kind) {
    case 'channel':
      return 'c:' + v.channelId;
    case 'voice':
      return (v.chat ? 'c:' : 'v:') + v.channelId;
    case 'home':
      return v.channelId ? 'c:' + v.channelId : v.page === 'requests' ? 'requests' : null;
  }
}

/**
 * The conversation the person is looking at: the main pane's, unless
 * Settings or a server's settings cover it, or a phone shows the list.
 */
export function shownKey(): string | null {
  const ui = useUi.getState();
  if (ui.settings || ui.admin) return null;
  if (readLayout().layout === 'phone' && usePanes.getState().pane !== 'main') return null;
  return viewKey(ui.hub);
}

/** The window (or tab) has the focus and is on screen. */
export function windowFocused(): boolean {
  if (typeof document === 'undefined') return false;
  return document.visibilityState !== 'hidden' && document.hasFocus();
}

/** Opens what a notification leads to, over whatever is open. */
export function openRoute(r: NotifyRoute | null): void {
  try {
    globalThis.focus?.();
  } catch {
    /* not allowed */
  }
  if (!r) return;
  const ui = useUi.getState();
  if (ui.settings) goSettings(null);
  if (ui.admin) ui.set({ admin: null });
  switch (r.to) {
    case 'channel':
      goHub({ kind: 'channel', channelId: r.channelId });
      break;
    case 'dm':
      goHub({ kind: 'home', channelId: r.channelId });
      break;
    case 'voice':
      goHub({ kind: 'voice', channelId: r.channelId });
      break;
    case 'requests':
      goHub({ kind: 'home', page: 'requests' });
      break;
  }
  if ((r.to === 'channel' || r.to === 'dm') && r.messageId)
    useUi.getState().set({ jump: { channelId: r.channelId, messageId: r.messageId } });
  openMain();
}
